import * as React from 'react';

import type { NotificationItemStatus } from './types';
import { CheckMark } from '@components/atoms/icons/check-mark';
import { Conflict } from '@components/atoms/icons/conflict';
import { LoadingSpinner } from '@components/atoms/loading-spinner/loading-spinner';
import { LoadingSpinnerSize } from '@components/atoms/loading-spinner/loading-spinner.types';

const STATUS_ICON_SLOT_CLASS = 'size-3.5 flex-shrink-0 flex items-center justify-center';

interface IStatusIconProps {
	status: NotificationItemStatus;
}

/**
 * Status glyph for a notification row. Loading uses the SM spinner constrained to
 * `size-3.5` so it matches success/warning/error icons instead of the 4px XS token.
 */
export const StatusIcon: React.FC<IStatusIconProps> = React.memo(({ status }) => {
	switch (status) {
		case 'loading':
			return (
				<span className={STATUS_ICON_SLOT_CLASS}>
					<LoadingSpinner size={LoadingSpinnerSize.SM} className="size-3.5" />
				</span>
			);
		case 'success':
			return (
				<span className={STATUS_ICON_SLOT_CLASS}>
					<div className="size-3.5 rounded-full bg-success/10 flex items-center justify-center">
						<CheckMark className="size-2 text-primary" />
					</div>
				</span>
			);
		case 'warning':
		case 'error':
			return (
				<span className={STATUS_ICON_SLOT_CLASS}>
					<Conflict className="size-3" />
				</span>
			);
		case 'pending':
			return null;
		default: {
			const _exhaustive: never = status;
			return _exhaustive;
		}
	}
});

StatusIcon.displayName = 'StatusIcon';
